import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, SectionTitle } from "../components/common";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import PricingJourney from "../components/pricing/PricingJourney";
import { publicApi } from "../lib/api";
import { getIcon } from "../lib/icons";

// The guided journey is the pricing page. `builder` stays as a seam for anything
// that needs to render a different tool inside the same page shell.
export default function BuildPackage({ builder, preview = false }) {
  const [cfg, setCfg] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    publicApi.packagePage().then(setCfg).catch(() => setFailed(true));
  }, []);

  if (failed) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center" data-testid="build-package-load-error">
        <Seo title="Pricing" noindex />
        <h1 className="font-display font-extrabold text-navy text-3xl">This page isn't available right now</h1>
        <p className="text-slate-600 mt-4">
          Sorry — something went wrong loading the page. Please try again shortly, or{" "}
          <Link to="/contact" className="text-orange font-semibold hover:underline">tell us what you need</Link> and we'll price it by hand.
        </p>
      </div>
    );
  }

  if (!cfg) {
    return (
      <div className="py-32 text-center" data-testid="build-package-loading">
        <Loader2 size={26} className="animate-spin text-orange mx-auto" />
      </div>
    );
  }

  const visibility = cfg.visibility || "live";
  const hidden = visibility !== "live";

  if (visibility === "off") {
    const u = cfg.unavailable || {};
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center" data-testid="build-package-off">
        <Seo title={cfg.seo_title || "Pricing"} noindex />
        <h1 className="font-display font-extrabold text-navy text-3xl">{u.heading || "This page isn't available at the moment"}</h1>
        <p className="text-slate-600 mt-4 leading-relaxed">{u.body}</p>
        <Link
          to={u.button_link || "/contact"}
          data-testid="build-package-off-cta"
          className="mt-8 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
        >
          {u.button_label || "Get a quote from us"} <ArrowRight size={17} />
        </Link>
      </div>
    );
  }

  const hero = cfg.hero || {};
  const covers = cfg.covers || {};
  const explainer = cfg.pricing_explainer || {};
  const discovery = cfg.discovery || {};
  const faqs = cfg.faqs || [];
  const cta = cfg.cta || {};

  const breadcrumb = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Pricing", path: "/pricing" },
  ]);

  const st = Object.fromEntries((cfg.page_sections || []).map((s) => [s.key, s]));
  const BG = { white: "bg-white", light: "bg-slate-50", navy: "bg-navy text-white" };
  const PAD = { normal: "py-20 md:py-24", compact: "py-12 md:py-16" };
  const WIDTH = { wide: "max-w-6xl", narrow: "max-w-4xl" };
  const dark = (key) => st[key]?.bg === "navy";
  const wrap = (key) => `${BG[st[key]?.bg] || BG.white} ${PAD[st[key]?.pad] || PAD.normal}`;
  const inner = (key) => `${WIDTH[st[key]?.width] || WIDTH.wide} mx-auto px-6`;

  const sections = {
    builder: (
      <section key="builder" className={wrap("builder")} data-testid="section-builder" data-bg={st.builder?.bg}>
        <div className={inner("builder")}>
          {builder ? builder(cfg) : <PricingJourney cfg={cfg} />}
        </div>
      </section>
    ),
    covers: (covers.cards || []).length > 0 && (
      <section key="covers" className={wrap("covers")} data-testid="section-covers" data-bg={st.covers?.bg}>
        <div className={inner("covers")}>
          <Reveal>
            <SectionTitle eyebrow={covers.eyebrow} title={covers.title} subtitle={covers.subtitle} light={dark("covers")} />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {covers.cards.map((c, i) => {
              const Icon = getIcon(c.icon);
              return (
                <Reveal key={i} delay={i * 60}>
                  <div
                    className={`h-full rounded-2xl border p-6 transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-lg ${
                      dark("covers") ? "border-white/15 bg-white/5" : "border-slate-200 bg-white"
                    }`}
                  >
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${dark("covers") ? "bg-orange text-white" : "bg-navy text-white"}`}>
                      <Icon size={20} />
                    </span>
                    <h3 className={`font-display font-bold text-base md:text-lg mt-4 ${dark("covers") ? "text-white" : "text-navy"}`}>{c.title}</h3>
                    <p className={`text-sm mt-2 leading-relaxed ${dark("covers") ? "text-slate-300" : "text-slate-600"}`}>{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    ),
    explainer: (explainer.paragraphs || []).length > 0 && (
      <section key="explainer" className={wrap("explainer")} data-testid="section-explainer" data-bg={st.explainer?.bg}>
        <div className={inner("explainer")}>
          <Reveal>
            {explainer.eyebrow && <span className="text-orange font-bold text-sm tracking-widest uppercase">{explainer.eyebrow}</span>}
            <h2 className={`font-display font-extrabold text-3xl md:text-4xl uppercase mt-3 leading-tight ${dark("explainer") ? "text-white" : "text-navy"}`}>
              {explainer.title}
            </h2>
            <div className={`mt-8 space-y-5 leading-relaxed ${dark("explainer") ? "text-slate-300" : "text-slate-600"}`}>
              {explainer.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              {cfg.disclaimer && <p className={`font-semibold ${dark("explainer") ? "text-white" : "text-navy"}`}>{cfg.disclaimer}</p>}
              {/* Software cost is a separate question people ask at this point. */}
              <p className="text-sm">
                Wondering what the software itself costs?{" "}
                <Link to="/services/xero-subscription" data-testid="link-xero-cost" className="text-orange font-semibold underline underline-offset-4 decoration-orange/40 hover:decoration-orange transition-colors">
                  See Xero's UK plans and prices
                </Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    ),
    discovery: (discovery.steps || []).length > 0 && (
      <section key="discovery" className={wrap("discovery")} data-testid="section-discovery" data-bg={st.discovery?.bg}>
        <div className={inner("discovery")}>
          <Reveal>
            <SectionTitle eyebrow={discovery.eyebrow} title={discovery.title} light={dark("discovery")} />
            <div className="mt-8 grid sm:grid-cols-3 gap-5">
              {discovery.steps.map((s, i) => (
                <div key={i} className={`rounded-2xl border p-6 ${dark("discovery") ? "border-white/15 bg-white/5" : "border-slate-200"}`}>
                  <span className="font-display font-extrabold text-orange text-2xl">{s.n}</span>
                  <h3 className={`font-display font-bold text-base mt-2 ${dark("discovery") ? "text-white" : "text-navy"}`}>{s.title}</h3>
                  <p className={`text-sm mt-2 leading-relaxed ${dark("discovery") ? "text-slate-300" : "text-slate-600"}`}>{s.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    ),
    faqs: faqs.length > 0 && (
      <section key="faqs" className={wrap("faqs")} data-testid="section-faqs" data-bg={st.faqs?.bg}>
        <div className={inner("faqs")}>
          <Reveal><SectionTitle eyebrow={cfg.faqs_eyebrow} title={cfg.faqs_title} light={dark("faqs")} /></Reveal>
          <dl className="mt-10 space-y-6" data-testid="build-package-faqs">
            {faqs.map((f, i) => (
              <div key={i} className={`rounded-2xl border p-6 ${dark("faqs") ? "border-white/15 bg-white/5" : "bg-white border-slate-200"}`}>
                <dt className={`font-display font-bold text-base md:text-lg ${dark("faqs") ? "text-white" : "text-navy"}`}>{f.q}</dt>
                <dd className={`text-sm mt-3 leading-relaxed ${dark("faqs") ? "text-slate-300" : "text-slate-600"}`}>{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    ),
    reviews: <GoogleReviewsSection key="reviews" testId="build-package-reviews" />,
    cta: (
      <section key="cta" className={wrap("cta")} data-testid="section-cta" data-bg={st.cta?.bg}>
        <div className={`${inner("cta")} text-center`}>
          <h2 className={`font-display font-extrabold text-3xl md:text-4xl mb-4 ${dark("cta") ? "text-white" : "text-navy"}`}>{cta.heading}</h2>
          <p className={`mb-8 ${dark("cta") ? "text-slate-300" : "text-slate-600"}`}>{cta.body}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to={cta.button_link || "/contact"}
              data-testid="build-package-contact-cta"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              {cta.button_label || "Let's Talk"} <ArrowRight size={18} />
            </Link>
          </div>
          <p className={`text-sm mt-6 ${dark("cta") ? "text-slate-400" : "text-slate-500"}`}>
            Already have an accountant?{" "}
            <Link to="/switching-accountants" data-testid="build-package-switching-link" className="text-orange font-semibold hover:underline">
              Switching to CASS is easy →
            </Link>
          </p>
        </div>
      </section>
    ),
  };

  const ordered = (cfg.page_sections || []).filter((s) => s.on !== false).map((s) => sections[s.key]).filter(Boolean);

  return (
    <div data-testid="build-package-page" data-visibility={visibility} data-preview={preview || undefined}>
      <Seo
        title={preview ? "Pricing journey (preview)" : cfg.seo_title || "Pricing"}
        description={preview ? undefined : cfg.seo_description}
        noindex={hidden || preview}
        jsonLd={hidden || preview ? undefined : [breadcrumb, faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a })))]}
      />

      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          {preview ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-orange/15 text-orange text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
              Preview — under review
            </span>
          ) : (
            hero.eyebrow && <span className="text-orange font-bold text-sm tracking-widest uppercase">{hero.eyebrow}</span>
          )}
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mt-3 max-w-3xl">
            {hero.heading}
          </h1>
          {hero.intro && <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed text-base md:text-lg">{hero.intro}</p>}
          {(hero.badges || []).length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              {hero.badges.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">{b}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {ordered}
    </div>
  );
}
